import React from "react";
import {
    Box,
    Typography,
    Container,
    Paper,
    Grid,
    Divider,
} from "@mui/material";

import SecurityIcon from "@mui/icons-material/Security";
import LockIcon from "@mui/icons-material/Lock";
import StorageIcon from "@mui/icons-material/Storage";
import UpdateIcon from "@mui/icons-material/Update";

import BgImage from "../../assets/img/subheader-back.jpg";

const sections = [
    {
        icon: <StorageIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "1. Information We Collect",
        content:
            "Critical Conquer may collect basic account information such as usernames, email addresses, IP logs, and gameplay activity. This information is used solely for account management, server security, and improving the player experience.",
    },
    {
        icon: <SecurityIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "2. How We Use Your Information",
        content:
            "Your information is used to manage accounts, improve gameplay services, provide support, detect suspicious activity, and maintain server stability. We do not sell, trade, or share your personal information with third parties.",
    },
    {
        icon: <LockIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "3. Data Security",
        content:
            "We take reasonable security measures to protect your account and personal information from unauthorized access, abuse, or disclosure. However, players are also responsible for keeping their passwords secure and private.",
    },
    {
        icon: <UpdateIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "4. Policy Updates",
        content:
            "Critical Conquer reserves the right to modify or update this Privacy Policy at any time. Any future changes will be posted on this page. Continued use of our services means you agree to the updated policy.",
    },
];

const PrivacyPolicy = () => {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundImage: `
                    linear-gradient(
                        rgba(5,5,5,0.92),
                        rgba(5,5,5,0.95)
                    ),
                    url(${BgImage})
                `,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                py: { xs: 5, md: 10 },
                px: 2,
            }}
        >
            <Container maxWidth="lg">
                {/* Header */}
                <Box textAlign="center" mb={6}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 900,
                            fontSize: {
                                xs: "2.2rem",
                                sm: "3rem",
                                md: "4rem",
                            },
                            mb: 2,
                            letterSpacing: 1,
                            background:
                                "linear-gradient(90deg, #ffd786 0%, #b88942 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            textShadow: "0px 0px 25px rgba(255,215,134,0.25)",
                        }}
                    >
                        Privacy Policy
                    </Typography>

                    <Typography
                        sx={{
                            color: "#bdbdbd",
                            maxWidth: 700,
                            mx: "auto",
                            fontSize: {
                                xs: "0.95rem",
                                md: "1.1rem",
                            },
                            lineHeight: 1.8,
                        }}
                    >
                        At Critical Conquer, we value your privacy and are
                        committed to protecting your information while providing
                        a safe and enjoyable gaming experience.
                    </Typography>
                </Box>

                {/* Cards */}
                <Grid container spacing={3}>
                    {sections.map((section, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    height: "100%",
                                    background:
                                        "rgba(20,20,20,0.82)",
                                    border:
                                        "1px solid rgba(255,215,134,0.15)",
                                    borderRadius: "18px",
                                    p: { xs: 3, md: 4 },
                                    backdropFilter: "blur(6px)",
                                    transition: "0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        border:
                                            "1px solid rgba(255,215,134,0.35)",
                                        boxShadow:
                                            "0 0 25px rgba(255,215,134,0.08)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 2,
                                        gap: 1.5,
                                    }}
                                >
                                    {section.icon}

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            color: "#ffd786",
                                            fontWeight: 700,
                                            fontSize: {
                                                xs: "1.1rem",
                                                md: "1.3rem",
                                            },
                                        }}
                                    >
                                        {section.title}
                                    </Typography>
                                </Box>

                                <Typography
                                    sx={{
                                        color: "#d6d6d6",
                                        lineHeight: 1.9,
                                        fontSize: {
                                            xs: "0.95rem",
                                            md: "1rem",
                                        },
                                    }}
                                >
                                    {section.content}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Additional Note */}
                <Paper
                    elevation={0}
                    sx={{
                        mt: 5,
                        background: "rgba(20,20,20,0.82)",
                        border:
                            "1px solid rgba(255,215,134,0.15)",
                        borderRadius: "18px",
                        p: { xs: 3, md: 4 },
                    }}
                >
                    <Typography
                        sx={{
                            color: "#d6d6d6",
                            lineHeight: 1.9,
                            fontSize: {
                                xs: "0.95rem",
                                md: "1rem",
                            },
                        }}
                    >
                        If you have questions regarding this Privacy Policy,
                        please contact our staff through our official Discord
                        server or website support channels.
                    </Typography>
                </Paper>

                {/* Footer */}
                <Divider
                    sx={{
                        borderColor: "rgba(255,255,255,0.08)",
                        my: 6,
                    }}
                />

                <Box textAlign="center">
                    <Typography
                        sx={{
                            color: "#8b8b8b",
                            fontSize: {
                                xs: "0.8rem",
                                md: "0.9rem",
                            },
                            lineHeight: 1.8,
                        }}
                    >
                        Last Updated: May 2026
                        <br />
                        Critical Conquer © All Rights Reserved
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;